import {
  VegaAccordion,
  VegaButton,
  VegaBox,
  VegaFlex,
  VegaInputSelect,
  VegaPageNotification,
} from '@globalpayments/vega-react';
import {
  KirbyFilterGroupNode,
  KirbyFilterNode,
  KirbyFilterPropertyDefinition,
  KirbyFilterValidationIssue,
} from '../../../types/kirbyFiltering';
import FilterGroup from './FilterGroup';

interface FilterBuilderProps {
  availableFilterTypes: string[];
  definitions: KirbyFilterPropertyDefinition[];
  filterType: string;
  isExpanded: boolean;
  isExecuting: boolean;
  issues: KirbyFilterValidationIssue[];
  root: KirbyFilterGroupNode;
  submissionUnavailableMessage?: string;
  onAddClause: (group: KirbyFilterGroupNode) => void;
  onAddGroup: (group: KirbyFilterGroupNode) => void;
  onFilterTypeChange: (filterType: string) => void;
  onNodeChange: (nodeId: string, update: (node: KirbyFilterNode) => KirbyFilterNode) => void;
  onExpand: (isExpanded: boolean) => void;
  onRemoveNode: (nodeId: string) => void;
  onReset: () => void;
  onSubmit: () => void;
  querySummary: string;
}

/** Composes the filter category, recursive clause groups, and explicit query actions. */
const FilterBuilder = ({
  availableFilterTypes,
  definitions,
  filterType,
  isExpanded,
  isExecuting,
  issues,
  root,
  submissionUnavailableMessage,
  onAddClause,
  onAddGroup,
  onFilterTypeChange,
  onNodeChange,
  onExpand,
  onRemoveNode,
  onReset,
  onSubmit,
  querySummary,
}: FilterBuilderProps) => {
  const generalIssue = issues.find((issue) => !issue.nodeId)?.message;

  return (
    <VegaAccordion
      accordionTitle={querySummary}
      data-testid="filter-builder"
      drawsBackground
      expand={isExpanded}
      expandIconAlign="right"
      onVegaExpand={(event) => onExpand(event.detail)}
      showExpandIcon>
      <VegaBox padding={{ top: 'size-16' }} slot="accordion-content">
        <VegaFlex direction="col" gap="size-20">
          <VegaInputSelect
            data-testid="filter-type"
            hint={generalIssue}
            isValid={generalIssue ? false : undefined}
            label="Filter category"
            onVegaChange={(event) => {
              if (typeof event.detail === 'string') onFilterTypeChange(event.detail);
            }}
            placeholder="Select a filter category"
            required
            selectType="single"
            source={availableFilterTypes.map((value) => ({ id: value, displayName: value }))}
            value={filterType}
          />
          {submissionUnavailableMessage && (
            <VegaPageNotification
              duration={0}
              message={submissionUnavailableMessage}
              notificationTitle="Category not executable"
              type="warning"
            />
          )}
          {filterType && definitions.length > 0 && (
            <FilterGroup
              definitions={definitions}
              depth={0}
              group={root}
              groupPath={[]}
              issues={issues}
              key={filterType}
              onAddClause={onAddClause}
              onAddGroup={onAddGroup}
              onNodeChange={onNodeChange}
              onRemoveNode={onRemoveNode}
            />
          )}
          <VegaFlex flexWrap="wrap" gap="size-12" justifyContent="end">
            <VegaFlex flexWrap="wrap" gap="size-8">
              <VegaButton label="Reset" onVegaClick={onReset} size="small" variant="secondary" />
              <VegaButton
                disabled={!filterType || Boolean(submissionUnavailableMessage)}
                icon="fa-solid fa-play"
                iconAlign="left"
                label="Run query"
                loading={isExecuting}
                onVegaClick={onSubmit}
                size="small"
                variant="primary"
              />
            </VegaFlex>
          </VegaFlex>
        </VegaFlex>
      </VegaBox>
    </VegaAccordion>
  );
};

export default FilterBuilder;
